import { CommonModule } from '@angular/common';
import { Component, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { LoadingService, NotificationService } from 'src/app/core';
import { Department, Machine } from 'src/app/modules/shared/interface/department.entity';
import { CreateOperatorDTO, GetOperatorDTO, UnassignedOperatorsDTO, OperatorsDTO } from 'src/app/modules/shared/interface/operator.entity';
import { shiftDTO, ShiftGetByDepartmentDTO } from 'src/app/modules/shared/interface/shift.entity';
import { DepartmentService } from 'src/app/modules/shared/services/department.service';
import { ModalService } from 'src/app/modules/shared/services/modal.service';
import { OperatorService } from 'src/app/modules/shared/services/operator.service';
import { ShiftService } from 'src/app/modules/shared/services/shift.service';
import { ShiftRosterService } from '../../services/shift-roster.service';
import { catchError, of, tap } from 'rxjs';
import { ShiftRosterDTO, ShiftRosterGetByDepartmentDTO } from '../../interfaces/shift-roster.entity';
import { MachineTypeService } from 'src/app/modules/shared/services/machine-type.service';
import { MachineTypeDTO } from 'src/app/modules/shared/interface/machine-type.entity';
import { Router } from '@angular/router';
import { GlobalDepartmentService } from 'src/app/core/services/global-department.service';

@Component({
  selector: 'app-shift-roster',
  imports: [
    FormsModule,
    CommonModule
  ],
  templateUrl: './shift-roster.component.html',
  styleUrl: './shift-roster.component.scss'
})
export class ShiftRosterComponent {

  departmentService = inject(DepartmentService);
  modal = inject(ModalService);
  operatorService = inject(OperatorService);
  notification = inject(NotificationService);
  shiftService = inject(ShiftService);
  shiftRosterService = inject(ShiftRosterService);
  loading = inject(LoadingService);
  machineTypeService = inject(MachineTypeService);
  router = inject(Router);
  globalDepartmentService = inject(GlobalDepartmentService);

  protected $$OperatorsDTO = signal<OperatorsDTO[]>([]);
  protected $$GetOperatorDTO = signal<GetOperatorDTO[]>([]);
  protected $$departmentDTO = signal<Department[]>([]);
  protected $$ShiftDTO = signal<shiftDTO[]>([]);
  protected Department = signal<Department[]>([]);
  protected $$availableMachines = signal<Machine[]>([]);
  protected $$shiftRosterGetByDepartmentDTO = signal<ShiftRosterGetByDepartmentDTO[]>([]);
  protected $$ShiftGetByDepartmentDTO = signal<ShiftGetByDepartmentDTO[]>([]);
  protected $$UnassignedOperatorsDTO = signal<UnassignedOperatorsDTO[]>([]);
  protected $$MachineTypeDTO = signal<MachineTypeDTO[]>([]);
  protected $$ShiftsByMachine = signal<Map<number, ShiftGetByDepartmentDTO[]>>(new Map());

  protected startDayIndex = signal(0);
  departmentId = this.globalDepartmentService.selectedDepartmentId;
  Year = signal(new Date().getFullYear());
  Month = signal(new Date().getMonth());
  selectedYear: number = this.Year();
  selectedMonth: number = this.Month();
  months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  years: number[] = [];
  operaterName: string = "";
  empNo: string = "";
  selectedOperaterId: number | null = null;
  isEditMode: boolean = false;
  calendarDays: Date[] = [];
  shiftId: number | null = null;
  operatorId: number | null = null;
  machineId: number | null = null;
  selectedDate: string = "";
  shiftRosterEntries: {
    date: string;
    shiftId: number | null;
    machineId: number | null;
    operatorId: number | null;
  }[] = [];

    protected searchParams = signal({
        department: this.globalDepartmentService.getDepartmentId() ?? -1,
        year: new Date().getFullYear(),
        month: new Date().getMonth() + 1,
    });

  addMode: 'daily' | 'bulk' = 'daily';
  bulkStartDate: string = '';
  bulkEndDate: string = '';
  shiftRosterId: number | null = null;
  searchTerm: string = '';
  dropdownOpenN: boolean = false
  section: string = '';
  existOperator: { operatorId: number; operator: string; shiftRosterId: number; shiftId:number }[] = [];
  rosterStatus: string = 'ACTIVE'; 

  constructor() {
    this.fetchDepartment();
    this.populateYears();
    this.getAllOperators();
    this.getAllShifts();
    this.getShiftRoster()
    this.initializeCurrentWeek();
    // this.getShiftsByDepartment(Number(this.departmentId))
    this.getAllMachineTypes();
  }

  addBulkShiftRosterEntry() {
    if (!this.bulkStartDate || !this.bulkEndDate || !this.shiftId || !this.operatorId || !this.machineId) {
      this.notification.set({ type: 'error', message: 'Fill all required fields for bulk add.' });
      return;
    }

    const start = new Date(this.bulkStartDate);
    const end = new Date(this.bulkEndDate);

    if (start > end) {
      this.notification.set({ type: 'error', message: 'Start date cannot be after end date.' });
      return;
    }

    const tempEntries: typeof this.shiftRosterEntries = [];

    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      const formattedDate = this.formatDate(d);
      tempEntries.push({
        date: formattedDate,
        shiftId: +this.shiftId,
        machineId: +this.machineId,
        operatorId: +this.operatorId
      });
    }

    this.shiftRosterEntries.push(...tempEntries);
    // this.notification.set({ type: 'success', message: `Bulk shift roster added successfully.` });

    this.shiftId = null;
    this.operatorId = null;
    this.machineId = null;
    this.bulkStartDate = '';
    this.bulkEndDate = '';
    this.searchTerm = '';

  }

  public fetchDepartment(): void {
    this.departmentService.getAllDepartments().subscribe({
      next: (result) => {
        if (result.data && result.data.departments) {
          this.$$departmentDTO.set(result.data.departments);

          if (this.departmentId()) {
            this.onDepartmentChange(this.departmentId()!);
          }
        }
      },
      error: (error) => {
        console.error('Error fetching departments:', error);
      }
    });
  }

  populateYears(): void {
    const startYear = 2025;
    this.years = Array.from({ length: 11 }, (_, i) => startYear + i);
  }

  openOperator() {
    this.modal.$$operatorManage.set(true);
    
  }

  openShift() {
    // this.modal.$$shiftManage.set(true);
    this.router.navigate(['shift-management']);
  }

  getMachineName(machineId: number | null): string {
    const machine = this.$$availableMachines().find(m => m.machineId === machineId);
    return machine ? machine.machineName : 'Unknown Machine';
  }

  getShiftName(shiftId: number | null, machineId: number | null): string {
    if (!shiftId || !machineId) return 'Unknown Shift';

    const shifts = this.$$ShiftsByMachine().get(machineId) || [];
    const shift = shifts.find(s => s.shiftId === shiftId);

    return shift ? shift.shiftName : 'Unknown Shift';
  }

  getOperatorName(operatorId: number | null): string {
    const operator = this.$$OperatorsDTO().find(o => o.operatorId === operatorId);
    return operator ? operator.operatorName : 'Unknown Operator';
  }


  getAllOperators() {
    const id = this.departmentId();

    this.operatorService.getAll(id).subscribe({
      next: (result) => {
        if (result.data.operators) {
          this.$$OperatorsDTO.set(result.data.operators);
        }
      },
      error: (error) => {
        console.error('Error fetching Operators:', error);
        this.notification.set({
          type: 'error',
          message: `Failed to Fetch Operators.`,
        });
      },
    });
  }

 addNewOperator() {
    if (!this.operaterName || !this.empNo || !this.departmentId() || !this.section) {
      this.notification.set({ type: 'error', message: `Fill required input.` });
      return;
    }

    const operator: CreateOperatorDTO = {
      name: this.operaterName,
      empNo: this.empNo,
      departmentId: this.departmentId()!,
      section: this.section.toLocaleLowerCase()
    };

    this.operatorService.create(operator).subscribe({
      next: (res) => {
        const message = res?.data?.message || 'Operator saved successfully';
        this.getAllOperators();
        this.resetForm();
        this.notification.set({ type: 'success', message });
      },
      error: (err) => {
        console.error('Error creating operator:', err);
        const message = err?.error?.message || 'Failed to save operator.';
        this.notification.set({ type: 'error', message });
      }
    });
  }

  resetForm() {
    this.operaterName = '';
    this.empNo = '';
    this.selectedOperaterId = null;
    this.isEditMode = false;
    this.section = '';
  }

  openEdit(id: number, empNo: string, name: string, dep:string, depId:number, section: string) {
    this.selectedOperaterId = id;
    this.empNo = empNo;
    this.operaterName = name;
    this.isEditMode = true;
    this.section = section;
  }

  updateOperator() {
    if (!this.operaterName || !this.empNo || !this.departmentId() || !this.section) {
      this.notification.set({ type: 'error', message: `Fill required input.` });
      return;
    }

    const id = this.selectedOperaterId;
    const updateOperator = {
      name: this.operaterName,
      empNo: this.empNo,
      section: this.section.toLocaleLowerCase(),
      departmentId: Number(this.departmentId())
    };

    if (id !== null) {
      this.operatorService.update(id, updateOperator).subscribe({
        next: (res) => {
          const message = res?.data?.message || 'Operator updated successfully';
          this.getAllOperators();
          this.resetForm();
          this.notification.set({ type: 'success', message });
        },
        error: (err) => {
          console.error('Error updating operator:', err);
          const message = err?.error?.message || 'Failed to update operator.';
          this.notification.set({ type: 'error', message });
        }
      });
    }
  }

  getAllShifts() {
    const id = this.departmentId();

    this.shiftService.getAll(id).subscribe({
      next: (result) => {
        if(result.data.shifts){
          this.$$ShiftDTO.set(result.data.shifts)          
        }
      },
      error: (error) => {
        console.error('Error fetching Operators:', error);
        this.notification.set({type: 'error', message: `Failed to Fetch Shifts.`});
      }
    });
  }

  getShiftsByDepartment(id: number){
    this.shiftService.getById(String(id)).subscribe({
      next: (result) => {
        if(result.data.shifts){
          this.$$ShiftGetByDepartmentDTO.set(result.data.shifts)
        }
      },
      error: (error) => {
        console.error('Error fetching Operators:', error);
        this.notification.set({type: 'error', message: `Failed to Fetch Shifts.`});
      }
    })
  }

  loadShiftsForMachines(machines: Machine[]): void {
    const shiftsMap = new Map<number, ShiftGetByDepartmentDTO[]>();
    let completed = 0;

    machines.forEach(machine => {
      this.shiftService.getById(String(machine.machineId)).subscribe({
        next: (result) => {
          if (result.data.shifts) {
            shiftsMap.set(machine.machineId, result.data.shifts);
          }
          completed++;
          if (completed === machines.length) {
            this.$$ShiftsByMachine.set(new Map(shiftsMap));
          }
        },
        error: (error) => {
          console.error(`Error fetching shifts for machine ${machine.machineId}:`, error);
          completed++;
          if (completed === machines.length) {
            this.$$ShiftsByMachine.set(new Map(shiftsMap));
          }
        }
      });
    });
  }

  addRoster() {
    this.modal.$$shiftRoster.set(true);
  }

  addShiftRosterEntry() {
    if (!this.selectedDate || !this.shiftId || !this.operatorId || !this.machineId) {
      this.notification.set({type: 'error', message: `Add all required fields.`});
      return;
    }

    this.shiftRosterEntries.push({
      date: this.selectedDate,
      shiftId: +this.shiftId,
      machineId: +this.machineId,
      operatorId: +this.operatorId
    });

    this.searchTerm = '';
    this.dropdownOpenN = false;
    this.machineId = null;
    this.selectedDate = '';
    this.shiftId = null;
    this.operatorId = null;

    // console.log('nnn',this.shiftRosterEntries);
    
  }

  saveShiftRosters() {
    this.shiftRosterService.create(this.shiftRosterEntries).subscribe({
      next: () => {
        this.shiftRosterEntries = [];
        this.machineId = null;
        this.selectedDate = "";
        this.searchTerm = '';
        this.modal.$$shiftRoster.set(false);
        this.notification.set({ type: 'success', message: `Shift Roster saved successfully.` });
        this.getShiftRoster();
      },
      error: (err) => {
        console.error(err);

        const message = err?.error?.message 
          ? err.error.message.replace('Error: [45000] ', '')
          : 'Failed to save shift roster. Please try again.';

        this.notification.set({ 
          type: 'error', 
          message 
        });
      }
    });
  }

  getShiftRoster() {
    this.loading.set(true);
    this.shiftRosterService.getShiftRosterByDep(this.searchParams()).pipe(
    tap(response => {
      this.loading.set(false);
      this.$$shiftRosterGetByDepartmentDTO.set([response.data]);
      // console.log(this.$$shiftRosterGetByDepartmentDTO())
    }),
    catchError(error => {
        this.loading.set(false);
        this.notification.set({ type: 'error', message: `Failed to fetch shift roster data` });
        console.error(error);
        return of(null);
    })
    ).subscribe();
  }

  onDepartmentChange(departmentId: number | string): void {
    const depId = Number(departmentId);
    this.globalDepartmentService.setDepartmentId(depId);
    this.searchParams.update(params => ({ ...params, department: depId }));

    const selectedDept = this.$$departmentDTO().find(d => d.departmentId === depId);
    if (selectedDept) {
      this.$$availableMachines.set(selectedDept.machines);
      this.loadShiftsForMachines(selectedDept.machines); // ← add this
    } else {
      this.$$availableMachines.set([]);
    }

    this.getShiftRoster();
    this.getAllOperators();
    this.getAllShifts();
  }

  updateCalendarYear(year: number): void {
    this.generateCalendar();
      this.searchParams.update(params => ({
      ...params,
      year: year
    }));
    this.getShiftRoster();
  }

  updateCalendarMonth(month: number): void {
    const newMonth = +month + 1
    this.generateCalendar();
     this.searchParams.update(params => ({
      ...params,
      month: newMonth
    }));
    this.getShiftRoster();
  }

  generateCalendar(): void {
    const year = this.selectedYear;
    const month = this.selectedMonth;
    const daysInMonthMap = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

    if (month === 1) {
      const isLeapYear = (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0);
      daysInMonthMap[1] = isLeapYear ? 29 : 28;
    }

    const daysInMonth = daysInMonthMap[month];

    if (this.startDayIndex() + 7 > daysInMonth) {
      this.startDayIndex.set(Math.max(0, daysInMonth - 7));
    }

    this.calendarDays = Array.from(
      { length: 7 },
      (_, i) => new Date(year, month, this.startDayIndex() + i + 1)
    );
  }

  initializeCurrentWeek(): void {
    const today = new Date();
    this.selectedYear = today.getFullYear();
    this.selectedMonth = today.getMonth();

    const currentDate = today.getDate();
    const startIndex = Math.floor((currentDate - 1) / 7) * 7;
    this.startDayIndex.set(startIndex);

    this.generateCalendar();
  }

  previousWeek(): void {
    if (this.startDayIndex() > 0) {
      this.startDayIndex.set(this.startDayIndex() - 7);
      this.generateCalendar();
    }
  }

  nextWeek(): void {
    const daysInMonth = new Date(this.selectedYear, this.selectedMonth + 1, 0).getDate();
    if (this.startDayIndex() + 7 < daysInMonth) {
      this.startDayIndex.set(this.startDayIndex() + 7);
      this.generateCalendar();
    }
  }

  get daysInCurrentMonth(): number {
    return new Date(this.selectedYear, this.selectedMonth + 1, 0).getDate();
  }

 isInDay(shiftRoster: any, day: Date): boolean {
    const [year, month, date] = [day.getFullYear(), day.getMonth() + 1, day.getDate()];
    const formattedDay = `${year}-${month.toString().padStart(2, '0')}-${date.toString().padStart(2, '0')}`;
    return shiftRoster.date === formattedDay;
  }

  formatDate(day: Date): string {
    const year = day.getFullYear();
    const month = (day.getMonth() + 1).toString().padStart(2, '0');
    const date = day.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${date}`;
  }

  

  // selectedShiftRosters = signal<{ operator: string; shiftRosterId: number }[]>([]);

  // addSelectedShiftRoster(item: { operator: string; shiftRosterId: number }) {
  //   const current = this.selectedShiftRosters();
  //   this.selectedShiftRosters.set([...current, item]);

  // }

  private isShiftType(shiftName: string, type: string): boolean {
    return shiftName.toLowerCase() === type.toLowerCase();
  }

  getOperatorWithIds(
    rosters: ShiftRosterDTO[],
    day: Date,
    shiftType: string
    ): { operatorId: number; operator: string; shiftRosterId: number; shiftId: number }[] {

    const formattedDay = this.formatDate(day);

    return rosters
      .filter(r =>
        r.date === formattedDay &&
        this.isShiftType(r.shift, shiftType)
      )
      .map(r => ({
        operatorId: r.operatorId,
        operator: r.operator,
        shiftRosterId: r.shiftRosterId,
        shiftId: r.shiftId
      }));
  }

  getShiftClass(
    rosters: ShiftRosterDTO[],
    day: Date,
    shiftType: string,
    rosterStatus: string
  ): string {

    return this.getOperators(rosters, day, shiftType, rosterStatus).length > 0
      ? 'bg-blue-200 text-gray-700'
      : 'bg-red-100 text-gray-500 italic';
  }


  deleteShiftRosterEntry(entry: { date: string; shiftId: number | null; machineId: number | null; operatorId: number | null; }) {
    this.shiftRosterEntries = this.shiftRosterEntries.filter(e => e !== entry);
  }

  loadUnassignedOperators(operatorIds: number[]) {
    this.operatorService.getUnassignedOperators(operatorIds)
      .pipe(
        tap(res => {
          this.$$UnassignedOperatorsDTO.set(res.data.operators);
          // console.log(this.$$UnassignedOperatorsDTO());
          
        }),
        catchError(err => {
          console.error(err);
          return of([]); 
        })
      )
      .subscribe();
  }

  getOperators(
    rosters: ShiftRosterDTO[],
    day: Date,
    shiftType: string,
    rosterStatus: string
  ): string[] {

    const formattedDay = this.formatDate(day);

    return rosters
      .filter(r =>
        r.date === formattedDay &&
        this.isShiftType(r.shift, shiftType) &&
        r.rosterStatus === rosterStatus   // ✅ FILTER HERE
      )
      .map(r => r.operator);
  }


  openOperatorUpdate(machineId: number, rosters: any[], day: Date, shift: string, op: { operatorId: number; operator: string; shiftRosterId: number; shiftId: number }[]) {
    const formattedDate = this.formatDate(day);
    
    
    this.existOperator = op;
    const operatorIds = this.existOperator.map(o => o.operatorId);
    this.loadUnassignedOperators(operatorIds)
    console.log(this.existOperator);
    
    
    const today = new Date();
    today.setHours(0, 0, 0, 0); 

    const selectedDay = new Date(day);
    selectedDay.setHours(0, 0, 0, 0);

    if (selectedDay < today) {
      this.notification.set({ type: 'error', message: 'Cannot Change Prevoius Dates' });
      return;
    }

    const found = rosters.find(r => r.date === formattedDate && r.shift === shift);

    if (found) {
      this.shiftRosterId = found.shiftRosterId;
      this.operatorId = null;
      // console.log(this.shiftRosterId);
    } else {
      this.shiftRosterId = null;
      // console.log('hi');
    }

    this.selectedDate = formattedDate;
    this.machineId = machineId;

    const shiftObj = this.$$ShiftGetByDepartmentDTO().find(s => s.shiftName === shift);
    this.shiftId = shiftObj ? shiftObj.shiftId : null;
    // console.log(this.shiftId);
    // console.log(this.selectedDate);

    this.modal.$$operatorUpdate.set(true);
  }



 updateOperatorAssignment() {

  if (!this.operatorId || !this.shiftId || !this.machineId || !this.selectedDate || !this.shiftRosterId || !this.rosterStatus) {
    this.notification.set({ type: 'error', message: 'Select all required fields.' });
    return;
  }

  // console.log(this.operatorId, this.shiftId, this.machineId, this.selectedDate );
  

  const assignment = {
    id: this.shiftRosterId,
    date: this.selectedDate,
    shiftId: this.shiftId,
    machineId: this.machineId,
    operatorId: +this.operatorId,
    status: this.rosterStatus
  };

  const payload = {
    shiftRosters: [assignment]
  };

  console.log(payload);
  
  if (this.shiftRosterId) {
    this.shiftRosterService.update(payload).subscribe({
      next: () => {
        this.notification.set({ type: 'success', message: 'Operator updated successfully.' });
        this.modal.$$operatorUpdate.set(false);
        this.getShiftRoster();
        this.searchTerm = '';
        this.operatorId = null;
        this.editingOperator = null;
        this.shiftRosterId = null;
      },
      error: (err) => {
        console.error(err);
        this.notification.set({ type: 'error', message: 'Failed to update operator.' });
      }
    });
  } else {
    this.shiftRosterService.create([payload]).subscribe({
      next: () => {
        this.notification.set({ type: 'success', message: 'Operator assigned successfully.' });
        this.modal.$$operatorUpdate.set(false);
        this.getShiftRoster();
        this.searchTerm = '';
        this.operatorId = null;
        this.editingOperator = null;
        this.shiftRosterId = null;
      },
      error: (err) => {
        console.error(err);
        this.notification.set({ type: 'error', message: 'Failed to assign operator.' });
      }
    });
  }
}

deleteOperatorAssignment(op: { operatorId: number; operator: string; shiftRosterId: number; shiftId: number }) {
  const payload = {
    shiftRosters: [{
      id: op.shiftRosterId,
      date: this.selectedDate,
      shiftId: op.shiftId,
      machineId: this.machineId!,
      operatorId: op.operatorId,
      status: 'DELETED'
    }]
  };

  this.shiftRosterService.update(payload).subscribe({
    next: () => {
      this.notification.set({ type: 'success', message: 'Operator removed successfully.' });
      this.existOperator = this.existOperator.filter(o => o.shiftRosterId !== op.shiftRosterId);
      this.getShiftRoster();
    },
    error: (err) => {
      console.error(err);
      this.notification.set({ type: 'error', message: 'Failed to remove operator.' });
    }
  });
}

  filteredOperatorsNew() {
      const term = this.searchTerm.toLowerCase();
      return this.$$UnassignedOperatorsDTO().filter(o =>
          o.operatorName.toLowerCase().includes(term)
      );
  }

   filteredOperators() {
      const term = this.searchTerm.toLowerCase();
      return this.$$OperatorsDTO().filter(o =>
          o.operatorName.toLowerCase().includes(term)
      );
  }

  selectOperator(operator: any) {
      this.operatorId = operator.operatorId;
      this.searchTerm = operator.operatorName;
      this.dropdownOpenN = false;
  }

  closeAddRoster() {
    this.modal.$$shiftRoster.set(false);
    this.searchTerm = '';
    this.dropdownOpenN = false;
    this.machineId = null;
    this.selectedDate = '';
    this.shiftId = null;
    this.operatorId = null;
    this.bulkStartDate = '';
    this.bulkEndDate = '';
    this.shiftRosterEntries = [];
  }

  closeUpdateModal() {
    this.modal.$$operatorUpdate.set(false);
    this.searchTerm = '';
    this.operatorId = null;
    this.editingOperator = null;
    this.shiftRosterId = null;
    this.dropdownOpenN = false;
  }

  editingOperator: { operator: string; shiftRosterId: number } | null = null;

  startEditOperator(op: { operator: string; shiftRosterId: number; shiftId: number}) {
    console.log(op);
    this.editingOperator = { ...op };
    this.searchTerm = op.operator;
    this.shiftRosterId = op.shiftRosterId;
    this.shiftId = op.shiftId;
    console.log(this.shiftId);

  }

  getAllMachineTypes() {
    this.machineTypeService.getAll().subscribe({
      next: (result) => {
        if(result.data){
          this.$$MachineTypeDTO.set(result.data)
          // console.log(this.$$MachineTypeDTO());
        }
      },
      error: (error) => {
        console.error('Error fetching Operators:', error);
        this.notification.set({type: 'error', message: `Failed to Fetch All amchine Types.`});
      }
    });
  }

  getShiftsForSelectedMachine(): ShiftGetByDepartmentDTO[] {
  if (!this.machineId) return [];
  return this.$$ShiftsByMachine().get(this.machineId) || [];
}

onMachineChange() {
  this.shiftId = null; // reset shift when machine changes
}

}
